import { useState } from 'react';
import { StyleSheet, Pressable, ActivityIndicator, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import Toast from 'react-native-root-toast';

import AppButton from '../components/AppButton.js';
import WebTooltip from '../components/WebTooltip.js';
import alert from '../components/alert.js';
import Util from '../util/util.js';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

export default function DeleteUserButton({
  user,
  organization,
  postDeleteAction,
  compact = false,
  // Multi-select props
  isSelected = false,
  onToggleSelect = null,
}) {
  const { t } = useTranslation('admin');

  const [username] = useState(user);
  const [confirmed, setConfirm] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);

  // Multi-select mode: clicking toggles selection instead of deleting
  const isMultiSelectMode = onToggleSelect !== null;

  const deleteUser = async () => {
    /*
     * Delete user
     */
    try {
      setUpdatingUser(true);
      setConfirm(false);

      await Util.post('UserManagement', '/users/delete', {
        body: {
          username: username,
          organization: organization,
        },
      }).then(() => {
        postDeleteAction(username);

        Toast.show(t('deleteUser.deleted'), {
          backgroundColor: AppStyles.colorSet.success,
          position: Toast.positions.BOTTOM,
          duration: 2500,
          textColor: AppStyles.colorSet.whiteColor,
        });
      });
    } catch (error) {
      setUpdatingUser(false);
      alert('Error', t('deleteUser.failed'));
    }
    setUpdatingUser(false);
  };

  const handlePress = () => {
    // In multi-select mode, just toggle selection
    if (isMultiSelectMode) {
      onToggleSelect(username);
      return;
    }

    // Original two-click confirm behavior
    if (confirmed) {
      deleteUser();
    } else {
      setConfirm(true);
    }
  };

  if (compact) {
    // Show selected state in multi-select mode, or confirmed state in two-click mode
    const isHighlighted = isMultiSelectMode ? isSelected : confirmed;
    const tooltipText = isMultiSelectMode
      ? (isSelected ? t('deleteUser.selectedForDeletion') : t('deleteUser.selectForDeletion'))
      : (confirmed ? t('deleteUser.clickToConfirm') : t('deleteUser.deleteUser'));

    const button = (
      <Pressable
        onPress={handlePress}
        disabled={updatingUser || user == null}
        style={({ pressed }) => [
          styles.iconButton,
          isHighlighted && styles.iconButtonConfirm,
          pressed && (isHighlighted ? styles.iconButtonConfirmPressed : styles.iconButtonPressed),
          (updatingUser || user == null) && styles.iconButtonDisabled,
        ]}
        accessibilityLabel={tooltipText}
        accessibilityHint={isMultiSelectMode ? t('deleteUser.toggleSelection') : t('deleteUser.deleteThisUser')}
      >
        {updatingUser ? (
          <ActivityIndicator size="small" color={AppStyles.colorSet.error} />
        ) : (
          <View style={styles.iconWrapper}>
            <Feather name="trash-2" size={18} color={isHighlighted ? AppStyles.colorSet.whiteColor : AppStyles.colorSet.error} />
          </View>
        )}
      </Pressable>
    );

    // Web hover tooltip (no-op on native)
    return <WebTooltip label={tooltipText}>{button}</WebTooltip>;
  }

  return (
    <AppButton
      title={confirmed ? t('deleteUser.confirmDelete') : t('common:actions.delete')}
      isBusy={updatingUser}
      isEnabled={!updatingUser && user!= null}
      onPress={handlePress}
      buttonStyle={styles.button}
      textStyle={styles.buttonText}
    />
  );
}
const styles = StyleSheet.create({
  button: {
    cursor: 'pointer',
    marginVertical: 10,
    borderRadius: AppStyles.borderRadii.md,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
    width: 70,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  buttonText: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  iconButton: {
    padding: AppStyles.spacing.sm,
    borderRadius: AppStyles.borderRadii.sm,
    backgroundColor: AppStyles.colorSet.errorLight,
    cursor: 'pointer',
  },
  iconButtonPressed: {
    backgroundColor: '#FEE2E2', // Slightly darker error light
  },
  iconButtonConfirm: {
    backgroundColor: AppStyles.colorSet.error,
  },
  iconButtonConfirmPressed: {
    backgroundColor: '#DC2626', // Darker error
  },
  iconButtonDisabled: {
    opacity: 0.5,
  },
  iconWrapper: {
    width: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
